import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardList, Download, Plus, Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  EmptyState,
  EntityDrawer,
  InlineAISuggestion,
  OperationsReportsButton,
  PageHeader,
  WorkflowStateBadge,
} from "../../../components/ui";
import { HrInitiateLink } from "../../../components/hr_operations";
import { ErrorPanel, Loading } from "../../../components/Loading";
import { api, ApiError } from "../../../lib/api";
import type { LeaveRequestDetail } from "../../../lib/api";
import { useApproverEmployeeId } from "../../../lib/approver";

// Pending Approvals (M15.9 · Session 3a.5 PR 5 adds bulk + on-behalf CTA).
// Lists leave_requests currently routed to the logged-in user. Inline
// approve / deny with reason. Now also: master-checkbox + bulk-approve
// / bulk-deny w/ shared reason + create-on-behalf deep-link to M30
// HR-initiated leave path.

function ApproveButton({
  requestId,
  onDone,
}: {
  requestId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const m = useMutation({
    mutationFn: () =>
      api.approveLeaveRequest(requestId, { actor: "approver" }),
    onSuccess: () => onDone(),
  });
  return (
    <button
      type="button"
      onClick={() => m.mutate()}
      disabled={m.isPending}
      className="flex items-center gap-1 rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
    >
      <Check size={14} />{" "}
      {m.isPending
        ? t("operations.leave_approving")
        : t("operations.leave_approve")}
    </button>
  );
}

function DenyDialog({
  requestId,
  onClose,
  onDone,
}: {
  requestId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.denyLeaveRequest(requestId, { actor: "approver", reason }),
    onSuccess: () => onDone(),
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.detail : String(err));
    },
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.leave_deny_request")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="deny-leave-form"
            disabled={m.isPending || !reason.trim()}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {m.isPending
              ? t("operations.leave_denying")
              : t("operations.leave_confirm_deny")}
          </button>
        </div>
      }
    >
      <form
        id="deny-leave-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMsg(null);
          m.mutate();
        }}
        className="flex flex-col gap-3 text-sm"
      >
        <label className="block">
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("operations.leave_reason_required")}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
        )}
      </form>
    </EntityDrawer>
  );
}

function BulkDenyDialog({
  requestIds,
  onClose,
  onDone,
}: {
  requestIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setErrorMsg(null);
    try {
      await Promise.all(
        requestIds.map((id) =>
          api.denyLeaveRequest(id, { actor: "approver", reason }),
        ),
      );
      onDone();
    } catch (e) {
      setErrorMsg(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("leave.approvals.bulk_deny_title", `Deny ${requestIds.length} requests`)}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {busy
              ? t("leave.approvals.bulk_denying", "Denying…")
              : t("leave.approvals.bulk_deny_confirm", `Deny ${requestIds.length}`)}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {t(
            "leave.approvals.bulk_deny_warning",
            "Each denial writes a workflow_event with this shared reason. Not reversible.",
          )}
        </p>
        <label className="block">
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("operations.leave_reason_required")}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
        )}
      </div>
    </EntityDrawer>
  );
}

function ApprovalCard({
  request,
  selected,
  onToggleSelect,
  onDone,
}: {
  request: LeaveRequestDetail;
  selected: boolean;
  onToggleSelect: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [denying, setDenying] = useState(false);
  return (
    <article className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4">
      <header className="flex items-start justify-between gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1"
          aria-label={t("leave.approvals.select_one", "Select request")}
        />
        <div className="flex-1">
          <div className="text-xs font-medium uppercase text-slate-500">
            {t("operations.leave_request_id_title", {
              id: request.id.slice(0, 8),
            })}
          </div>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="font-medium">
              {request.start_date} → {request.end_date}
            </span>
            <WorkflowStateBadge state={request.workflow_state} />
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {t("operations.leave_employee_label")}: {request.employee_id.slice(0, 8)} ·{" "}
            {t("operations.leave_days_label")}: {request.days_requested}
            {request.balance_snapshot_at_submit && (
              <>
                {" "}· {t("operations.leave_balance_at_submit_short")}: {request.balance_snapshot_at_submit}
              </>
            )}
          </div>
          {request.reason && (
            <p className="mt-1 text-xs italic text-slate-700">
              "{request.reason}"
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDenying(true)}
            className="flex items-center gap-1 rounded border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
          >
            <X size={14} /> {t("operations.leave_deny_short")}
          </button>
          <ApproveButton requestId={request.id} onDone={onDone} />
        </div>
      </header>
      {denying && (
        <DenyDialog
          requestId={request.id}
          onClose={() => setDenying(false)}
          onDone={() => {
            setDenying(false);
            onDone();
          }}
        />
      )}
    </article>
  );
}

export function LeaveApprovalsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const approverEmployeeId = useApproverEmployeeId();
  // M3X.LIST-AND-REPORT-FIXES PR 1: when no approver is identified
  // (HR view), show ALL pending approvals across the tenant. When an
  // approver IS identified, filter to that approver's queue.
  const approverFilter = approverEmployeeId || undefined;
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showBulkDeny, setShowBulkDeny] = useState(false);

  const listQ = useQuery({
    queryKey: ["leave-approvals", approverFilter ?? "all"],
    queryFn: () =>
      api.listLeaveRequests({
        approverEmployeeId: approverFilter,
        state: "pending_approval",
      }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["leave-approvals"] });
    setSelected(new Set());
  };

  const items: LeaveRequestDetail[] = listQ.data ?? [];

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const stalePending = useMemo(() => {
    const now = Date.now();
    return items.filter((r) => {
      const created = r.submitted_at ?? r.created_at;
      if (!created) return false;
      const ageDays = Math.floor((now - new Date(created).getTime()) / 86_400_000);
      return ageDays >= 5;
    }).length;
  }, [items]);

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      await Promise.all(
        ids.map((id) => api.approveLeaveRequest(id, { actor: "approver" })),
      );
      refresh();
    } catch (e) {
      window.alert(
        t(
          "leave.approvals.bulk_approve_error",
          `Bulk approve hit an error: ${e instanceof Error ? e.message : String(e)}. Some requests may have been approved; refresh to see current state.`,
        ),
      );
      refresh();
    }
  };

  if (listQ.isLoading) return <Loading />;
  if (listQ.error)
    return <ErrorPanel message={(listQ.error as Error).message} />;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("leave.approvals_title")}
        subtitle={t(
          items.length === 1
            ? "leave.approvals_subtitle"
            : "leave.approvals_subtitle_plural",
          { count: items.length },
        )}
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.leave"), href: "/operations/leave/requests" },
          { label: t("leave.approvals_breadcrumb") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <HrInitiateLink transactionType="leave" />
            <OperationsReportsButton
              domain="leaves"
              domainLabel={t("nav.leave", "Leaves")}
            />
          </div>
        }
      />

      {/* §4.4 — Create on behalf CTA + bulk action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-text-tertiary">
          <input
            type="checkbox"
            checked={selected.size === items.length && items.length > 0}
            onChange={() => {
              if (selected.size === items.length) setSelected(new Set());
              else setSelected(new Set(items.map((r) => r.id)));
            }}
            aria-label={t("leave.approvals.select_all", "Select all")}
          />
          <span>
            {selected.size > 0
              ? t("leave.approvals.selected_count", `${selected.size} selected`)
              : t("leave.approvals.select_all_hint", "Select all visible requests")}
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/operations/transactions/leave/new")}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t("leave.approvals.cta.create_on_behalf", "Create on behalf")}
        </button>
      </div>

      {stalePending > 0 && (
        <InlineAISuggestion
          agent="tashghil"
          icon={<Sparkles className="h-4 w-4" />}
          severity="warning"
          headline={t(
            "leave.approvals.suggestion.stale_headline",
            `${stalePending} request${stalePending === 1 ? "" : "s"} pending >5 days.`,
          )}
          body={t(
            "leave.approvals.suggestion.stale_body",
            "Tashghil suggests bulk-approving the routine ones (single-day · within balance · no conflicts) and reviewing only the flagged ones individually.",
          )}
          primaryAction={{
            label: t("leave.approvals.suggestion.select_all", "Select all to bulk-approve"),
            onClick: () => setSelected(new Set(items.map((r) => r.id))),
          }}
          suggestionKey="leave-approvals-stale"
        />
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-blue bg-brand-blue/5 px-3 py-2 text-sm">
          <span className="font-medium text-brand-deep">
            {t("leave.approvals.bulk.selected", `${selected.size} selected`)}
          </span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={bulkApprove}
              className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <Check className="h-3 w-3" />
              {t("leave.approvals.bulk.approve_all", `Approve all ${selected.size}`)}
            </button>
            <button
              type="button"
              onClick={() => setShowBulkDeny(true)}
              className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
            >
              <X className="h-3 w-3" />
              {t("leave.approvals.bulk.deny_all", `Deny all ${selected.size}`)}
            </button>
            <button
              type="button"
              onClick={() =>
                window.alert(
                  t(
                    "leave.approvals.bulk.export_note",
                    `Export ${selected.size} requests as CSV for offline review.`,
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
            >
              <Download className="h-3 w-3" />
              {t("leave.approvals.bulk.export", "Export selected")}
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2 py-1 text-xs font-medium text-text-tertiary hover:bg-surface-sunken"
            >
              {t("common.clear", "Clear")}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          headline={t("leave.approvals_empty_title")}
          description={t("leave.approvals_empty_body")}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((r) => (
            <ApprovalCard
              key={r.id}
              request={r}
              selected={selected.has(r.id)}
              onToggleSelect={() => toggleSelect(r.id)}
              onDone={refresh}
            />
          ))}
        </div>
      )}

      {showBulkDeny && (
        <BulkDenyDialog
          requestIds={Array.from(selected)}
          onClose={() => setShowBulkDeny(false)}
          onDone={() => {
            setShowBulkDeny(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
